import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Zap } from "lucide-react";
import SEO from "../components/SEO";

const standardFlavors = [
  { name: "Pink Vanilla", color: "#FFB6C1", description: "Classic sweet vanilla" },
  { name: "Blue Raspberry", color: "#00BFFF", description: "Tangy berry blast" },
  { name: "Grape", color: "#9370DB", description: "Rich grape flavor" },
  { name: "Orange Citrus", color: "#FFA500", description: "Bright citrus burst" },
  { name: "Watermelon", color: "#FF6B6B", description: "Fresh summer taste" },
  { name: "Cherry", color: "#DC143C", description: "Bold cherry sweetness" },
  { name: "Birthday Cake", color: "#FFE4E1", description: "Celebration classic" },
  { name: "Bubblegum", color: "#FF69B4", description: "Fun nostalgic flavor" },
  { name: "Strawberry", color: "#FF6384", description: "Sweet berry delight" },
  { name: "Green Apple", color: "#90EE90", description: "Tart and refreshing" },
  { name: "Lemon", color: "#FFF44F", description: "Zesty citrus punch" },
  { name: "Cotton Candy Classic", color: "#E6E6FA", description: "Traditional favorite" },
];


export default function FlavorsPage() {
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-pink-50 to-white" data-testid="flavors-page">
      <SEO 
        title="Cotton Candy Flavors"
        description="Explore our wholesale cotton candy flavors, including non-toxic, naturally UV-activated Birthday Cake. For retailers, resorts, and entertainment venues."
        keywords="cotton candy flavors, wholesale flavors, UV cotton candy, dye-free cotton candy"
        canonical="/flavors"
      />
      
      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-[#FF9EAA] text-white text-sm font-bold px-4 py-2 rounded-full mb-6">
            WHOLESALE FLAVOR GALLERY
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A4A4A] mb-6">
            Our <span className="gradient-text">Flavors</span>
          </h1>
          <p className="text-lg md:text-xl text-[#888888] leading-relaxed max-w-2xl mx-auto">
            Classic flavors for wholesale orders. Mix and match to create the right selection for your customers.
          </p>
        </div>
      </section>

      {/* Standard Flavors */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-[#4A4A4A] mb-8 text-center">Standard Flavors</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {standardFlavors.map((flavor) => (
              <div 
                key={flavor.name}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow text-center"
                style={{ borderTop: `4px solid ${flavor.color}` }}
                data-testid={`flavor-${flavor.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-4"
                  style={{ backgroundColor: flavor.color, opacity: 0.4 }}
                ></div>
                <h3 className="font-bold text-[#4A4A4A] mb-1">{flavor.name}</h3>
                <p className="text-sm text-[#888888]">{flavor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UV-Reactive Flavors */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-purple-500/30 text-purple-200 text-sm font-bold px-4 py-2 rounded-full mb-6">
              <Zap size={16} />
              SIGNATURE PRODUCT
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">UV-Activated Birthday Cake</h2>
            <p className="text-purple-200">Our one UV flavor—dye-free, non-toxic, and naturally activated under UV light</p>
          </div>
          
          <div className="max-w-md mx-auto mb-10">
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-purple-400/30"
              data-testid="uv-flavor-birthday-cake"
            >
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-4 animate-pulse"
                style={{ backgroundColor: "#FFE4E1", boxShadow: "0 0 24px #E9D5FF" }}
              ></div>
              <h3 className="font-bold text-xl mb-2">Birthday Cake</h3>
              <p className="text-sm text-purple-200">Dye-free • Non-toxic • Naturally UV-activated</p>
              <p className="text-sm text-purple-300 mt-3">Looks like classic cotton candy in normal light and glows under blacklight thanks to natural ingredients—no artificial dyes.</p>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/uv-cotton-candy">
              <Button 
                className="bg-white text-purple-900 hover:bg-purple-100 rounded-full px-8 py-4 font-semibold"
              >
                Learn More About UV Cotton Candy
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#4A4A4A] mb-4">Ready to Order?</h2>
          <p className="text-lg text-[#888888] mb-8">
            Contact us for wholesale pricing and minimum order requirements.
          </p>
          <Link to="/wholesale">
            <Button 
              className="bg-[#FF9EAA] hover:bg-[#FF8FAB] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Get Wholesale Pricing
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
